import { forwardRef } from 'react';
import { cn, focusRing } from '../utils/cn';
import { Field, controlBaseClasses } from './Field';
import { Icon } from './Icon';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  'size' | 'children'
> {
  label: string;
  hideLabel?: boolean;
  hint?: string;
  error?: string | null;
  options: SelectOption[];
  placeholder?: string;
  containerClassName?: string;
}

/**
 * A native `<select>`. Deliberately not a custom listbox: the reference screens
 * use plain dropdowns and the native control gets keyboard, mobile and screen
 * reader behaviour right for free.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    label,
    hideLabel,
    hint,
    error,
    options,
    placeholder,
    required,
    className,
    containerClassName,
    ...rest
  },
  ref,
) {
  return (
    <Field
      label={label}
      hideLabel={hideLabel}
      hint={hint}
      error={error}
      required={required}
      className={containerClassName}
    >
      {({ controlId, describedBy, invalid }) => (
        <div className="relative flex items-center">
          <select
            ref={ref}
            id={controlId}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            required={required}
            className={cn(
              controlBaseClasses,
              'h-9 cursor-pointer appearance-none py-0 pl-3 pr-8',
              focusRing,
              className,
            )}
            {...rest}
          >
            {placeholder ? (
              <option value="" disabled>
                {placeholder}
              </option>
            ) : null}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          <Icon
            name="chevron-down"
            size={15}
            className="pointer-events-none absolute right-2.5 text-content-muted"
          />
        </div>
      )}
    </Field>
  );
});
